import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Star, ShoppingCart, Plus, Minus, Heart,
  Truck, Shield, RotateCcw, Check, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TVA_RATE = 0.20;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [activeImage, setActiveImage] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    fetchAll();
  }, [id]);

  useEffect(() => {
    if (user && user !== false) checkWishlist();
  }, [user, id]);

  const fetchAll = async () => {
    try {
      const [pRes, rRes, sRes] = await Promise.all([
        axios.get(`${API}/products/${id}`),
        axios.get(`${API}/reviews/${id}`),
        axios.get(`${API}/products/${id}/similar?limit=4`),
      ]);
      setProduct(pRes.data);
      setReviews(rRes.data);
      setSimilar(sRes.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Produit introuvable');
      navigate('/products');
    }
  };

  const checkWishlist = async () => {
    try {
      const { data } = await axios.get(`${API}/wishlist/check/${id}`, { withCredentials: true });
      setInWishlist(data.in_wishlist);
    } catch (e) { /* ignore */ }
  };

  const toggleWishlist = async () => {
    if (!user || user === false) {
      toast.error('Veuillez vous connecter');
      navigate('/login');
      return;
    }
    try {
      if (inWishlist) {
        await axios.delete(`${API}/wishlist/${id}`, { withCredentials: true });
        toast.success('Retiré des favoris');
        setInWishlist(false);
      } else {
        await axios.post(`${API}/wishlist`, { product_id: id }, { withCredentials: true });
        toast.success('Ajouté aux favoris');
        setInWishlist(true);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAddToCart = async () => {
    if (!user || user === false) {
      toast.error('Veuillez vous connecter pour ajouter au panier');
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/cart`,
        { product_id: id, quantity },
        { withCredentials: true }
      );
      toast.success('Produit ajouté au panier');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user || user === false) {
      navigate('/login');
      return;
    }
    try {
      await axios.post(
        `${API}/reviews`,
        { product_id: id, rating, comment },
        { withCredentials: true }
      );
      toast.success('Avis publié');
      setComment('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
      </div>
    );
  }

  const hasDiscount = product.old_price && product.old_price > product.price;
  const priceHT = product.price / (1 + TVA_RATE);
  const images = product.images || [];

  return (
    <div className="min-h-screen bg-surface py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Link to="/" className="hover:text-brand-orange">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/products" className="hover:text-brand-orange">Produits</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/products?category=${product.category}`} className="hover:text-brand-orange capitalize">
            {product.category?.replace(/-/g, ' ')}
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground truncate">{product.name}</span>
        </nav>

        <div className="bg-white rounded-sm border border-border overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 p-4 md:p-6 lg:p-8">
            {/* Gallery */}
            <div>
              <div
                className="aspect-square bg-surface rounded-sm overflow-hidden border border-border cursor-zoom-in relative"
                onClick={() => setZoom(!zoom)}
                data-testid="product-main-image"
              >
                <img
                  src={images[activeImage]}
                  alt={product.name}
                  className={`w-full h-full object-cover transition-transform duration-300 ${zoom ? 'scale-150' : ''}`}
                />
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {hasDiscount && (
                    <span className="bg-destructive text-white text-sm font-bold px-3 py-1 rounded-sm">
                      -{Math.round(((product.old_price - product.price) / product.old_price) * 100)}%
                    </span>
                  )}
                  {product.is_new && (
                    <span className="bg-brand-orange text-white text-sm font-bold px-3 py-1 rounded-sm">
                      NOUVEAU
                    </span>
                  )}
                </div>
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 mt-3">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImage(idx)}
                      className={`w-20 h-20 rounded-sm overflow-hidden border-2 ${
                        idx === activeImage ? 'border-brand-orange' : 'border-border'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              {product.brand && (
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
                  {product.brand}
                </span>
              )}
              <h1
                className="text-2xl md:text-3xl font-bold mt-1 mb-3"
                style={{ fontFamily: 'IBM Plex Sans' }}
                data-testid="product-title"
              >
                {product.name}
              </h1>

              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.floor(product.rating)
                          ? 'fill-brand-orange text-brand-orange'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="ml-2 font-medium text-sm">{product.rating}</span>
                </div>
                <button
                  onClick={() => setActiveTab('reviews')}
                  className="text-sm text-muted-foreground hover:text-brand-orange underline"
                >
                  ({product.reviews_count} avis)
                </button>
                {product.specifications?.['Référence'] && (
                  <span className="text-xs text-muted-foreground">
                    Réf. {product.specifications['Référence']}
                  </span>
                )}
              </div>

              {/* Price Box */}
              <div className="bg-surface border border-border rounded-sm p-4 mb-4">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-3xl md:text-4xl font-bold text-brand-orange" data-testid="product-price">
                    {product.price.toFixed(2)} €
                  </span>
                  {hasDiscount && (
                    <span className="text-lg text-muted-foreground line-through">
                      {product.old_price.toFixed(2)} €
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">TTC</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Soit {priceHT.toFixed(2)} € HT
                </p>
              </div>

              {/* Stock */}
              <div className="flex items-center gap-2 mb-4">
                {product.stock > 0 ? (
                  <>
                    <Check className="w-5 h-5 text-success" />
                    <span className="text-success font-semibold">En stock</span>
                    <span className="text-sm text-muted-foreground">
                      ({product.stock} disponibles)
                    </span>
                  </>
                ) : (
                  <span className="text-destructive font-semibold">Rupture de stock</span>
                )}
              </div>

              {/* Reassurance */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Truck className="w-4 h-4 text-brand-orange flex-shrink-0" />
                  <span>Livraison 24-48h</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Shield className="w-4 h-4 text-brand-orange flex-shrink-0" />
                  <span>Garantie 2 ans</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <RotateCcw className="w-4 h-4 text-brand-orange flex-shrink-0" />
                  <span>Retour 30 jours</span>
                </div>
              </div>

              {product.stock > 0 && (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-medium">Quantité :</span>
                    <div className="flex items-center border border-border rounded-sm">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="p-2 hover:bg-surface"
                        data-testid="decrease-quantity-button"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-4 font-medium" data-testid="quantity-display">{quantity}</span>
                      <button
                        onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                        className="p-2 hover:bg-surface"
                        data-testid="increase-quantity-button"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleAddToCart}
                      disabled={loading}
                      className="flex-1 btn-primary flex items-center justify-center gap-2 px-6 py-4 rounded-sm font-bold text-base disabled:opacity-50"
                      data-testid="add-to-cart-button"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      {loading ? 'Ajout...' : 'Ajouter au panier'}
                    </button>
                    <button
                      onClick={toggleWishlist}
                      className={`px-4 py-4 rounded-sm border-2 transition-colors ${
                        inWishlist
                          ? 'bg-brand-orange text-white border-brand-orange'
                          : 'border-border hover:border-brand-orange'
                      }`}
                      data-testid="toggle-wishlist-button"
                      aria-label="Ajouter aux favoris"
                    >
                      <Heart className={`w-5 h-5 ${inWishlist ? 'fill-white' : ''}`} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-t border-border">
            <div className="px-4 md:px-6 lg:px-8 pt-6">
              <div className="flex gap-1 border-b border-border overflow-x-auto">
                {[
                  { id: 'description', label: 'Description' },
                  { id: 'specs', label: 'Caractéristiques' },
                  { id: 'reviews', label: `Avis (${reviews.length})` },
                  { id: 'faq', label: 'FAQ' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-brand-orange text-brand-orange'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="py-6 min-h-[200px]">
                {activeTab === 'description' && (
                  <div className="prose max-w-none">
                    <p className="text-foreground leading-relaxed">{product.description}</p>
                  </div>
                )}

                {activeTab === 'specs' && product.specifications && (
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <tr key={key} className="border-b border-border">
                          <td className="py-3 pr-4 font-medium text-muted-foreground w-1/3">{key}</td>
                          <td className="py-3 text-foreground">{value}</td>
                        </tr>
                      ))}
                      <tr className="border-b border-border">
                        <td className="py-3 pr-4 font-medium text-muted-foreground">Marque</td>
                        <td className="py-3 text-foreground">{product.brand || 'BatiPro'}</td>
                      </tr>
                      {product.ean && (
                        <tr className="border-b border-border">
                          <td className="py-3 pr-4 font-medium text-muted-foreground">Code EAN</td>
                          <td className="py-3 text-foreground font-mono">{product.ean}</td>
                        </tr>
                      )}
                      <tr className="border-b border-border">
                        <td className="py-3 pr-4 font-medium text-muted-foreground">Catégorie</td>
                        <td className="py-3 text-foreground capitalize">{product.category?.replace(/-/g, ' ')}</td>
                      </tr>
                    </tbody>
                  </table>
                )}

                {activeTab === 'reviews' && (
                  <div className="space-y-4">
                    {reviews.length === 0 ? (
                      <p className="text-muted-foreground">Aucun avis pour le moment</p>
                    ) : (
                      reviews.map((review) => (
                        <div key={review.id} className="border border-border rounded-sm p-4" data-testid={`review-${review.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">{review.user_name}</span>
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-4 h-4 ${
                                  i < review.rating ? 'fill-brand-orange text-brand-orange' : 'text-gray-300'
                                }`} />
                              ))}
                            </div>
                          </div>
                          {review.comment && <p className="text-foreground text-sm">{review.comment}</p>}
                          <p className="text-xs text-muted-foreground mt-2">
                            {new Date(review.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'faq' && (
                  <div className="space-y-3">
                    {[
                      { q: 'Quel est le délai de livraison ?', a: 'Nos commandes sont expédiées sous 24-48h et livrées en 2 à 3 jours ouvrés via Colissimo.' },
                      { q: 'Quelle est la garantie sur ce produit ?', a: 'Tous nos produits bénéficient d\'une garantie constructeur de 2 ans minimum.' },
                      { q: 'Puis-je retourner ce produit ?', a: 'Oui, vous disposez de 30 jours pour retourner votre produit sans justification.' },
                      { q: 'Comment est calculé le prix HT/TTC ?', a: 'Tous les prix affichés incluent la TVA française à 20%. Le prix HT est calculé en divisant le prix TTC par 1.20.' },
                    ].map((item, idx) => (
                      <details key={idx} className="border border-border rounded-sm group">
                        <summary className="cursor-pointer p-4 font-medium hover:bg-surface flex items-center justify-between">
                          {item.q}
                          <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                        </summary>
                        <p className="px-4 pb-4 text-sm text-muted-foreground">{item.a}</p>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Similar Products */}
        {similar.length > 0 && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold mb-5" style={{ fontFamily: 'IBM Plex Sans' }}>
              Produits similaires
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {similar.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
